import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { consulta, pedir } from '../api/nucleo'
import { Aviso } from '../componentes/ui'

/**
 * Cómo cobra la tienda de esta web, en avisos que no se pueden pasar por alto (auditoría del
 * 29-09-2026): la pasarela de prueba encendida con la web publicada (cualquiera se lleva un
 * pedido «pagado» sin pagar), avisos de la pasarela con la firma mala, cobros que la
 * pasarela no confirma, un importe que no cuadra, y una tienda sin pasarela ni mensaje que
 * deja al comprador sin saber cómo pagar. Lo cuenta el núcleo (`GET /api/pedidos/cobro`):
 * aquí solo se dice, y se lleva a donde se arregla.
 */
export type EstadoDeCobro = {
  modoDeVenta: 'catalogo' | 'tienda'
  publicada: boolean
  pasarela: null | { pasarela: string; nombre: string; modo: 'produccion' | 'pruebas' }
  mensajeDePedido: boolean
  firmasInvalidas: number
  importesDistintos: number
  sinConfirmar: number
  puedeConfigurar: boolean
}

export const useEstadoDeCobro = (sitio: number) =>
  useQuery({
    queryKey: ['cobro', sitio],
    queryFn: () => pedir<EstadoDeCobro>(`/pedidos/cobro${consulta({ sitio })}`),
    staleTime: 30_000,
    // Sin tienda en esta web es un 404: no hay nada que avisar, y no se reintenta.
    retry: false,
  })

const veces = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`

/**
 * Los avisos. `que` elige cuáles: en Pedidos y en Pasarelas, todos; en Ajustes, solo los
 * que se arreglan ahí (el mensaje de pedido) y el de la pasarela de prueba.
 */
export const AvisosDeCobro = ({
  sitio,
  espacio,
  que = 'todos',
  mensajeEscrito,
  catalogo,
}: {
  /** En Ajustes, lo que hay escrito ahora en el formulario manda sobre lo guardado. */
  catalogo?: boolean
  espacio: number
  mensajeEscrito?: boolean
  que?: 'ajustes' | 'todos'
  sitio: number
}) => {
  const { data } = useEstadoDeCobro(sitio)
  if (!data || (catalogo ?? data.modoDeVenta === 'catalogo')) return null
  const conMensaje = mensajeEscrito ?? data.mensajeDePedido
  const base = `/e/${espacio}/s/${sitio}`
  const aPasarelas = data.puedeConfigurar ? (
    <Link className="font-medium underline" to={`${base}/pagos`}>
      Ir a la pasarela
    </Link>
  ) : (
    <span>Díselo a quien administra la cuenta.</span>
  )
  const avisos: Array<{ clave: string; tono: 'aviso' | 'error'; texto: ReactNode }> = []
  const p = data.pasarela

  if (p?.pasarela === 'prueba') {
    avisos.push(
      data.publicada
        ? {
            clave: 'prueba',
            tono: 'error',
            texto: (
              <>
                <strong>La pasarela de prueba está activa y tu web está publicada:</strong> cualquiera puede llevarse un pedido «pagado» sin pagar nada. Pon una pasarela de verdad o desactívala. {aPasarelas}
              </>
            ),
          }
        : { clave: 'prueba', tono: 'aviso', texto: <>Cobras con la pasarela de prueba: no se cobra nada de verdad. Antes de publicar la web, pon una pasarela de verdad. {aPasarelas}</> },
    )
  }
  if (que === 'todos' && p && p.pasarela !== 'prueba' && p.modo === 'pruebas' && data.publicada) {
    avisos.push({ clave: 'modo', tono: 'aviso', texto: <>{p.nombre} está en modo de pruebas: tus compradores no pueden pagar de verdad. Cuando la hayas probado, pásala a «Producción» con las claves de producción. {aPasarelas}</> })
  }
  if (que === 'todos' && data.firmasInvalidas > 0 && p) {
    avisos.push({
      clave: 'firmas',
      tono: 'error',
      texto: (
        <>
          En los últimos 7 días han llegado {veces(data.firmasInvalidas, 'aviso', 'avisos')} de {p.nombre} con la firma mal, y no se han podido aplicar: esos pagos no se han confirmado. Revisa el «Secreto de los avisos» de la pasarela. {aPasarelas}
        </>
      ),
    })
  }
  if (que === 'todos' && data.sinConfirmar > 0 && p) {
    avisos.push({
      clave: 'sin-confirmar',
      tono: 'aviso',
      texto: (
        <>
          {veces(data.sinConfirmar, 'cobro lleva', 'cobros llevan')} más de una hora sin confirmar. Si el comprador pagó, lo normal es que {p.nombre} no nos esté mandando sus avisos: revisa la dirección de avisos y sus eventos. {aPasarelas}
        </>
      ),
    })
  }
  if (que === 'todos' && data.importesDistintos > 0) {
    avisos.push({ clave: 'importes', tono: 'error', texto: <>{veces(data.importesDistintos, 'pedido tiene', 'pedidos tienen')} un pago por un importe que no cuadra con el pedido: no se ha marcado como pagado. Ábrelo y compruébalo en la pasarela antes de enviar nada.</> })
  }
  if (!p && !conMensaje) {
    avisos.push({
      clave: 'como-pagar',
      tono: 'aviso',
      texto: (
        <>
          <strong>Tu tienda no cobra en línea y no dice cómo pagar.</strong> Al terminar un pedido, el comprador lee que le escribirás con la forma de pago. Escribe cómo pagar (transferencia, Yape, contra entrega…) en{' '}
          {que === 'ajustes' ? (
            '«Qué se le dice al terminar», en la pestaña Tienda'
          ) : (
            <Link className="font-medium underline" to={`${base}/ajustes`}>
              Ajustes → Tienda
            </Link>
          )}
          {data.puedeConfigurar ? (
            <>
              , o{' '}
              <Link className="font-medium underline" to={`${base}/pagos`}>
                configura una pasarela
              </Link>
            </>
          ) : null}
          .
        </>
      ),
    })
  }
  if (avisos.length === 0) return null
  return (
    <div className="mb-4 flex flex-col gap-2">
      {avisos.map((a) => (
        <Aviso key={a.clave} tono={a.tono}>
          {a.texto}
        </Aviso>
      ))}
    </div>
  )
}
